import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useVaultBalance, formatUSDT } from "../hooks/useVault";
import { useUsdcBalance, useGamesAllowance, useGamesActions, formatUSDC } from "../hooks/useGames";
import { useSevenDayPnl } from "../hooks/useSevenDayPnl";
import BalanceDelta from "./BalanceDelta";
import { useEnableSessionSigner } from "../auth/sessionSigner";
import { ACTIVE_CHAIN, isVaultDeployed, isGamesDeployed } from "../contracts/chains";
import type { AuthUser } from "../hooks/useAuth";
import DepositModal from "./DepositModal";
import WithdrawModal from "./WithdrawModal";
import SendUsdcModal from "./SendUsdcModal";
import { SOLANA_NETWORK } from "../contracts/solana/config";
import SolanaWalletPanel from "./SolanaWalletPanel";

/* ============================================================
   Wallet panel — new "Wallet Card" design from the Claude
   Design "Hupper - Pong" handoff. Two layouts, chosen by
   whether the user still holds a legacy HupperVault balance:

   - v1 (Wallet Card.html)    → has legacy vault: balance +
     Quick Play toggle + legacy vault card + Receive/Send.
   - v2 (Wallet Card v2.html) → no legacy vault: bigger balance
     with delta + 7-day sparkline + Top up + Receive/Send/Swap.

   Visual only — all behaviour is preserved from the previous
   panel: drag, Esc-close, live USDC balance, session-signer
   Quick Play, legacy withdraw, copy/explorer, network, send,
   deposit, sign out, sub-modals.

   The bundle's warm palette + keyframes are scoped under `wc-`.
   ============================================================ */

interface WalletPanelProps {
  walletAddress: string | null;
  user: AuthUser | null;
  onClose: () => void;
  onSignOut: () => void;
  initialView?: "wallet" | "deposit" | "withdraw" | "send";
}

const PANEL_WIDTH = 380;
const PANEL_DEFAULT_TOP = 80;
const PANEL_RIGHT_MARGIN = 24;

function defaultPosition(): { x: number; y: number } {
  if (typeof window === "undefined") return { x: 0, y: PANEL_DEFAULT_TOP };
  return {
    x: Math.max(16, window.innerWidth - PANEL_WIDTH - PANEL_RIGHT_MARGIN),
    y: PANEL_DEFAULT_TOP,
  };
}

// ── Inline icons (match the handoff SVGs) ───────────────────────────
const CloseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
);
const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" /><path d="M5 15V5a2 2 0 012-2h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="#5fcf80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const ExplorerIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M14 4h6v6M20 4l-10 10M19 13v6a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const ZapIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" /></svg>
);
const ReceiveIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const SendIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const SwapIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M17 1l4 4-4 4M3 11V9a4 4 0 014-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 01-4 4H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" /></svg>
);
const SignOutIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const WC_STYLES = `
.wc-card {
  --bg:#0a0806; --card:#0e0c0a; --card-2:#14110e; --line:#211c16; --line-2:#2d251c;
  --text:#f5efe6; --muted:#8a7f72; --muted-2:#5a5247; --accent:#ff5722; --accent-2:#ff8a4a;
  --green:#5fcf80; --green-line:rgba(95,207,128,0.30);
  border-radius: 18px;
  background:
    radial-gradient(ellipse 80% 40% at 50% 0%, rgba(255,87,34,0.06), transparent 70%),
    linear-gradient(180deg, rgba(20,17,14,0.95), rgba(10,8,6,0.98));
  box-shadow: 0 40px 100px -30px rgba(0,0,0,0.6), 0 0 0 1px var(--line-2), inset 0 1px 0 rgba(255,138,74,0.06);
  position: relative; overflow: hidden; backdrop-filter: blur(12px);
  color: var(--text); font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif;
  display: flex; flex-direction: column;
}
.wc-card::before { content:''; position:absolute; top:0; left:0; right:0; height:2px;
  background: linear-gradient(90deg, transparent 0%, var(--accent) 30%, var(--accent-2) 50%, var(--accent) 70%, transparent 100%);
  opacity:0.85; box-shadow:0 0 12px rgba(255,87,34,0.4); }
.wc-card::after { content:''; position:absolute; top:0; left:0; right:0; height:2px; width:80px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
  animation: wc-sweep 4.5s linear infinite; pointer-events:none; }
.wc-header { display:flex; align-items:center; justify-content:space-between; padding:18px 20px; border-bottom:1px solid var(--line); }
.wc-header-left { display:flex; align-items:center; gap:12px; }
.wc-grip { display:grid; grid-template-columns:repeat(2,3px); grid-template-rows:repeat(3,3px); gap:3px; opacity:0.45; transition:opacity 160ms ease; }
.wc-header-left:hover .wc-grip { opacity:0.8; }
.wc-grip span { width:3px; height:3px; border-radius:999px; background:var(--muted); }
.wc-header-id { display:flex; flex-direction:column; line-height:1; min-width:0; }
.wc-label { font-family:'Geist Mono',ui-monospace,monospace; font-size:10px; letter-spacing:1.8px; color:var(--muted-2); font-weight:600; margin-bottom:5px; }
.wc-handle { font-family:'Geist Mono',ui-monospace,monospace; font-size:13.5px; color:var(--text); font-weight:600; letter-spacing:0.1px; max-width:230px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.wc-close { width:30px; height:30px; border-radius:999px; border:1px solid var(--line-2); background:transparent; color:var(--muted); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:all 160ms ease; flex-shrink:0; }
.wc-close:hover { color:var(--text); border-color:var(--muted); background:rgba(255,255,255,0.03); }
.wc-body { padding:18px 20px 20px; display:flex; flex-direction:column; gap:14px; flex:1 1 auto; min-height:0; overflow-y:auto; }
/* Keep children at natural height so a tall card scrolls instead of
   compressing overflow:hidden sections (which would clip their content). */
.wc-body > * { flex-shrink:0; }
.wc-balance { position:relative; border-radius:14px; padding:18px 20px; overflow:hidden;
  background: radial-gradient(ellipse 80% 60% at 100% 0%, rgba(255,87,34,0.11), transparent 70%), linear-gradient(180deg, rgba(28,22,18,0.62), rgba(14,11,9,0.72));
  border:1px solid rgba(255,87,34,0.23); }
.wc-balance.lg { padding:22px 22px 20px; }
.wc-corner-line { position:absolute; top:0; left:14px; right:14px; height:1px; background:linear-gradient(90deg, transparent, rgba(255,138,74,0.5), transparent); }
.wc-balance-label-row { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; }
.wc-balance-label { font-family:'Geist Mono',ui-monospace,monospace; font-size:10px; letter-spacing:1.8px; color:var(--muted); font-weight:600; }
.wc-live { display:inline-flex; align-items:center; gap:6px; font-family:'Geist Mono',ui-monospace,monospace; font-size:9.5px; letter-spacing:1.2px; color:var(--green); font-weight:600; }
.wc-live::before { content:''; width:5px; height:5px; border-radius:999px; background:var(--green); box-shadow:0 0 0 2px rgba(95,207,128,0.2); animation:wc-pulse 1.6s ease-in-out infinite; }
.wc-balance-value { display:flex; align-items:baseline; gap:6px; margin-bottom:8px; }
.wc-cur-sign { font-family:'Geist Mono',ui-monospace,monospace; font-size:16px; color:var(--muted); font-weight:500; transform:translateY(-10px); }
.wc-balance.lg .wc-cur-sign { font-size:22px; transform:translateY(-14px); }
.wc-amount { font-family:'Geist Mono',ui-monospace,monospace; font-size:44px; font-weight:700; color:var(--text); letter-spacing:-1.5px; line-height:1.1; padding-bottom:2px;
  font-variant-numeric:tabular-nums; background:linear-gradient(180deg, #fff8ef 0%, #f5efe6 70%, #d8cdb5 100%); background-clip:text; -webkit-background-clip:text; -webkit-text-fill-color:transparent; }
.wc-amount.lg { font-size:60px; letter-spacing:-2.2px; }
.wc-cur { font-family:'Geist Mono',ui-monospace,monospace; font-size:12px; color:var(--muted); margin-left:4px; letter-spacing:0.6px; transform:translateY(-12px); font-weight:500; }
.wc-balance.lg .wc-cur { font-size:13px; transform:translateY(-22px); }
.wc-balance-sub { font-size:11.5px; color:var(--muted); line-height:1.4; }
.wc-balance-sub b { color:var(--text); font-weight:500; }
.wc-delta { display:flex; align-items:center; gap:8px; margin-top:2px; }
.wc-delta-badge { display:inline-flex; align-items:center; gap:5px; padding:3px 8px; border-radius:5px; background:rgba(95,207,128,0.10); border:1px solid rgba(95,207,128,0.30); font-family:'Geist Mono',ui-monospace,monospace; font-size:11px; font-weight:700; color:var(--green); letter-spacing:0.2px; }
.wc-delta-badge.neg { background:rgba(255,90,90,0.10); border-color:rgba(255,90,90,0.32); color:#ff7a7a; }
.wc-delta-sub { font-family:'Geist Mono',ui-monospace,monospace; font-size:10.5px; color:var(--muted); letter-spacing:0.5px; }
.wc-spark-wrap { margin-top:16px; height:56px; position:relative; }
.wc-spark-labels { display:flex; justify-content:space-between; margin-top:6px; font-family:'Geist Mono',ui-monospace,monospace; font-size:9.5px; color:var(--muted-2); letter-spacing:0.7px; }
.wc-qp { display:grid; grid-template-columns:auto 1fr auto; align-items:center; gap:14px; padding:14px 16px; border-radius:12px; border:1px solid var(--green-line); background:linear-gradient(180deg, rgba(95,207,128,0.06), rgba(95,207,128,0.02)); }
.wc-qp.off { border-color:rgba(255,176,74,0.32); background:linear-gradient(180deg, rgba(255,176,74,0.06), rgba(255,176,74,0.02)); }
.wc-qp-icon { width:30px; height:30px; border-radius:8px; background:rgba(95,207,128,0.10); border:1px solid var(--green-line); display:inline-flex; align-items:center; justify-content:center; color:var(--green); }
.wc-qp.off .wc-qp-icon { background:rgba(255,176,74,0.10); border-color:rgba(255,176,74,0.32); color:var(--accent-2); }
.wc-qp-text { min-width:0; }
.wc-qp-t { font-size:13px; font-weight:600; color:var(--green); margin-bottom:2px; letter-spacing:0.1px; }
.wc-qp.off .wc-qp-t { color:var(--accent-2); }
.wc-qp-d { font-size:11px; color:var(--muted); line-height:1.4; }
.wc-qp-err { font-size:10px; color:var(--red, #ff5a5a); margin-top:3px; }
.wc-qp-toggle { width:32px; height:18px; border-radius:999px; background:rgba(95,207,128,0.20); border:1px solid var(--green-line); position:relative; cursor:pointer; flex-shrink:0; padding:0; }
.wc-qp-toggle::after { content:''; position:absolute; top:2px; right:2px; width:12px; height:12px; border-radius:999px; background:var(--green); box-shadow:0 0 8px rgba(95,207,128,0.5); transition:all 200ms ease; }
.wc-qp.off .wc-qp-toggle { background:rgba(138,127,114,0.18); border-color:var(--line-2); }
.wc-qp.off .wc-qp-toggle::after { right:auto; left:2px; background:var(--muted); box-shadow:none; }
.wc-qp.off .wc-qp-toggle:not(:disabled):hover::after { background:var(--accent-2); }
.wc-qp-toggle:disabled { cursor:default; }
.wc-legacy { border-radius:12px; padding:14px 16px; border:1px solid var(--line); background:rgba(255,255,255,0.014); }
.wc-legacy-row1 { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:6px; gap:10px; }
.wc-legacy-label { font-family:'Geist Mono',ui-monospace,monospace; font-size:10px; letter-spacing:1.8px; color:var(--muted-2); font-weight:600; margin-bottom:4px; }
.wc-legacy-amount { font-family:'Geist Mono',ui-monospace,monospace; font-size:18px; font-weight:700; color:var(--text); letter-spacing:-0.3px; font-variant-numeric:tabular-nums; }
.wc-legacy-amount span { font-size:11px; color:var(--muted); margin-left:4px; font-weight:500; }
.wc-legacy-withdraw { background:transparent; border:1px solid rgba(255,87,34,0.35); border-radius:8px; padding:6px 11px; font-family:'Geist Mono',ui-monospace,monospace; font-size:10.5px; font-weight:700; letter-spacing:1px; color:var(--accent); cursor:pointer; transition:all 160ms ease; flex-shrink:0; align-self:center; }
.wc-legacy-withdraw:hover { background:rgba(255,87,34,0.08); border-color:var(--accent); color:var(--accent-2); }
.wc-legacy-desc { font-size:11px; color:var(--muted); line-height:1.4; }
.wc-warn { border-radius:10px; padding:11px 13px; border:1px solid rgba(255,176,74,0.3); background:rgba(255,176,74,0.06); font-size:11.5px; color:#e9c08a; line-height:1.5; }
.wc-warn code { font-family:'Geist Mono',ui-monospace,monospace; color:var(--text); }
.wc-sect-label { font-family:'Geist Mono',ui-monospace,monospace; font-size:10px; letter-spacing:1.8px; color:var(--muted-2); font-weight:600; margin-top:4px; margin-bottom:-4px; }
.wc-address-row { display:flex; align-items:center; border-radius:10px; border:1px solid var(--line); background:rgba(255,255,255,0.014); padding:0 4px 0 12px; height:40px; transition:border-color 160ms ease; }
.wc-address-row:hover { border-color:var(--line-2); }
.wc-address-value { flex:1; min-width:0; font-family:'Geist Mono',ui-monospace,monospace; font-size:12px; color:var(--text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; letter-spacing:0.1px; }
.wc-icon-btn { width:30px; height:30px; border-radius:6px; border:none; background:transparent; color:var(--muted); cursor:pointer; display:inline-flex; align-items:center; justify-content:center; transition:all 160ms ease; flex-shrink:0; text-decoration:none; }
.wc-icon-btn:hover { color:var(--text); background:rgba(255,255,255,0.04); }
.wc-icon-btn:disabled { opacity:0.35; cursor:default; }
.wc-network-row { display:flex; align-items:center; justify-content:space-between; padding:0 14px; border-radius:10px; border:1px solid var(--line); background:rgba(255,255,255,0.014); height:40px; }
.wc-network-name { display:inline-flex; align-items:center; gap:9px; font-size:12.5px; color:var(--text); font-weight:500; }
.wc-network-dot { width:7px; height:7px; border-radius:999px; background:var(--green); box-shadow:0 0 0 2px rgba(95,207,128,0.18), 0 0 8px var(--green); }
.wc-network-mode { font-family:'Geist Mono',ui-monospace,monospace; font-size:9.5px; letter-spacing:1.4px; color:var(--muted-2); font-weight:600; padding:3px 7px; border-radius:4px; background:rgba(138,127,114,0.08); border:1px solid var(--line); }
.wc-actions { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:6px; }
.wc-actions-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:8px; }
.wc-btn-primary { appearance:none; border:none; border-radius:12px; cursor:pointer; height:48px;
  background:linear-gradient(180deg, #ff7b3d 0%, #ff5722 55%, #e94a18 100%); color:#fff; font-family:inherit; font-size:14px; font-weight:600; letter-spacing:0.1px;
  display:flex; align-items:center; justify-content:center; gap:8px; position:relative; overflow:hidden;
  box-shadow:0 10px 30px -14px rgba(255,87,34,0.6), 0 0 0 1px rgba(255,140,80,0.25) inset, 0 1px 0 rgba(255,255,255,0.15) inset; transition:all 180ms ease; }
.wc-btn-primary.tall { height:50px; }
.wc-btn-primary::before { content:''; position:absolute; top:0; left:0; right:0; height:50%; background:linear-gradient(180deg, rgba(255,255,255,0.18), transparent); pointer-events:none; }
.wc-btn-primary:hover { transform:translateY(-1px); box-shadow:0 18px 50px -18px rgba(255,87,34,0.7), 0 0 0 1px rgba(255,140,80,0.4) inset, 0 1px 0 rgba(255,255,255,0.15) inset; }
.wc-btn-primary:disabled { opacity:0.4; cursor:default; transform:none; }
.wc-btn-secondary { appearance:none; border:1px solid var(--line-2); border-radius:12px; cursor:pointer; height:48px; background:rgba(255,255,255,0.014); color:var(--text); font-family:inherit; font-size:14px; font-weight:500; letter-spacing:0.1px; display:flex; align-items:center; justify-content:center; gap:8px; transition:all 180ms ease; }
.wc-btn-secondary:hover { border-color:var(--muted); background:rgba(255,255,255,0.025); }
.wc-btn-secondary:disabled { opacity:0.4; cursor:default; }
.wc-btn-action { appearance:none; border:1px solid var(--line-2); border-radius:12px; cursor:pointer; height:60px; background:rgba(255,255,255,0.012); color:var(--text); font-family:inherit; font-size:13px; font-weight:500; letter-spacing:0.1px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:5px; transition:all 180ms ease; }
.wc-btn-action:not(:disabled):hover { border-color:var(--accent); background:rgba(255,87,34,0.06); transform:translateY(-1px); }
.wc-btn-action:not(:disabled):hover .wc-btn-icon { color:var(--accent); background:rgba(255,87,34,0.12); border-color:rgba(255,87,34,0.3); }
.wc-btn-action:disabled { opacity:0.45; cursor:default; }
.wc-btn-icon { width:22px; height:22px; border-radius:6px; background:rgba(255,255,255,0.03); border:1px solid var(--line); color:var(--muted); display:inline-flex; align-items:center; justify-content:center; transition:all 180ms ease; }
.wc-signout { background:transparent; border:none; color:var(--muted); cursor:pointer; padding:14px; margin:2px auto 0; font-family:'Geist Mono',ui-monospace,monospace; font-size:11.5px; font-weight:500; letter-spacing:0.8px; display:flex; align-items:center; justify-content:center; gap:8px; transition:color 160ms ease; }
.wc-signout:hover { color:var(--text); }
@keyframes wc-pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.55; transform:scale(0.7); } }
@keyframes wc-sweep { 0% { transform:translateX(-100%); } 100% { transform:translateX(100%); } }
@media (prefers-reduced-motion: reduce) { .wc-card::after, .wc-live::before { animation:none; } }
`;

export default function WalletPanel({
  walletAddress,
  user,
  onClose,
  onSignOut,
  initialView = "wallet",
}: WalletPanelProps) {
  // On Solana, hand off to the dedicated panel BEFORE any EVM hook runs —
  // this keeps the EVM money hooks (useUsdcBalance/useVaultBalance/etc.) from
  // ever executing on the Solana path, so rules-of-hooks holds. EVM stays the
  // untouched default (SOLANA_NETWORK is false unless VITE_NETWORK=solana).
  if (SOLANA_NETWORK) {
    return (
      <SolanaWalletPanel
        walletAddress={walletAddress}
        user={user}
        onClose={onClose}
        onSignOut={onSignOut}
        initialView={initialView}
      />
    );
  }

  const [copied, setCopied] = useState(false);
  const [view, setView] = useState<"wallet" | "deposit" | "withdraw" | "send">(initialView);
  const [pos, setPos] = useState(defaultPosition);
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // The user's wallet IS the balance — read USDC straight from the wallet.
  const usdc = useUsdcBalance(walletAddress, 15_000);
  const allowance = useGamesAllowance(walletAddress, 30_000);
  const session = useEnableSessionSigner();
  const gamesDeployed = isGamesDeployed();
  // Global one-time USDC authorisation. Allowance is per-spender
  // (HupperGames), not per-game, so a single approve here unlocks every
  // paid game. We approve MaxUint256, so a large remaining allowance means
  // "already authorised". 1000 USDC threshold distinguishes a real
  // infinite approve from a tiny leftover from some other flow.
  const { approveGames, status: approveStatus } = useGamesActions();
  const AUTH_THRESHOLD = 1_000_000_000n; // 1000 USDC (6 decimals)
  const gamesAuthorized = gamesDeployed && allowance.value >= AUTH_THRESHOLD;

  // Legacy HupperVault balance — only surfaced if non-zero. Drives which
  // card layout we show (v1 with the legacy section vs the slim v2).
  const vault = useVaultBalance(walletAddress, 30_000);
  const vaultDeployed = isVaultDeployed();
  const hasLegacyBalance = vaultDeployed && vault.value > 0n;
  const isV2 = !hasLegacyBalance;

  // Esc closes the panel — but only when no sub-modal is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && view === "wallet") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, view]);

  // Drag listeners (attached only while dragging).
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;
      const maxX = window.innerWidth - 80;
      const maxY = window.innerHeight - 60;
      setPos({
        x: Math.max(-PANEL_WIDTH + 80, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  const startDrag = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-no-drag]")) return;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    setDragging(true);
    e.preventDefault();
  };

  const copy = async () => {
    if (!walletAddress) return;
    try {
      await navigator.clipboard.writeText(walletAddress);
    } catch {
      /* clipboard blocked */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const explorerUrl =
    walletAddress && ACTIVE_CHAIN.explorerUrl
      ? `${ACTIVE_CHAIN.explorerUrl}/address/${walletAddress}`
      : null;

  const hasBalance = gamesDeployed && !usdc.loading;
  const amountText = hasBalance ? formatUSDC(usdc.value) : usdc.loading ? "…" : "—";
  const canSend = gamesDeployed && usdc.value > 0n;
  const handle = user?.displayName ?? "Embedded wallet";
  const showQuickPlay = !isV2 && gamesDeployed && session.ready;

  // Real 7-day P&L from on-chain match history → drives the v2 delta +
  // sparkline (see BalanceDelta).
  const { pnlMinor: pnl7d } = useSevenDayPnl(walletAddress);

  return (
    <>
      <style>{WC_STYLES}</style>
      <div
        role="dialog"
        aria-label="Wallet"
        className="wc-card"
        style={{
          position: "fixed",
          zIndex: 50,
          left: pos.x,
          top: pos.y,
          width: "min(380px, calc(100vw - 24px))",
          maxHeight: "calc(100vh - 96px)",
          userSelect: dragging ? "none" : "auto",
        }}
      >
        {/* Header (drag handle) */}
        <div className="wc-header" onMouseDown={startDrag} style={{ cursor: dragging ? "grabbing" : "grab" }}>
          <div className="wc-header-left">
            <span className="wc-grip" aria-hidden="true">
              <span /><span /><span /><span /><span /><span />
            </span>
            <div className="wc-header-id">
              <span className="wc-label">WALLET</span>
              <span className="wc-handle" title={handle}>{handle}</span>
            </div>
          </div>
          <button data-no-drag onClick={onClose} aria-label="Close wallet panel" className="wc-close">
            <CloseIcon />
          </button>
        </div>

        <div className="wc-body">
          {/* Balance */}
          <div className={isV2 ? "wc-balance lg" : "wc-balance"}>
            <div className="wc-corner-line" />
            <div className="wc-balance-label-row">
              <span className="wc-balance-label">BALANCE</span>
              {hasBalance && <span className="wc-live">LIVE</span>}
            </div>
            <div className="wc-balance-value">
              {hasBalance && <span className="wc-cur-sign">$</span>}
              <span className={isV2 ? "wc-amount lg" : "wc-amount"}>{amountText}</span>
              {hasBalance && <span className="wc-cur">USDC</span>}
            </div>
            {isV2 ? (
              <>
                <BalanceDelta pnlMinor={pnl7d} balanceMinor={usdc.value} />
              </>
            ) : (
              <div className="wc-balance-sub"><b>Ready to play</b> · in your wallet</div>
            )}
          </div>

          {/* Quick Play (v1, when applicable) */}
          {showQuickPlay && (
            <div className={session.enabled ? "wc-qp" : "wc-qp off"}>
              <span className="wc-qp-icon"><ZapIcon /></span>
              <div className="wc-qp-text">
                <div className="wc-qp-t">{session.enabled ? "Quick Play enabled" : "Quick Play disabled"}</div>
                <div className="wc-qp-d">
                  {session.enabled
                    ? "Auto-lock & settle on your behalf — no in-game popups"
                    : session.enabling
                    ? "Authorizing…"
                    : "Authorize Hupper once to play with no popups"}
                </div>
                {session.error && <div className="wc-qp-err">{session.error}</div>}
              </div>
              <button
                data-no-drag
                className="wc-qp-toggle"
                aria-label="Toggle Quick Play"
                aria-pressed={session.enabled}
                disabled={session.enabled || session.enabling}
                onClick={session.enabled ? undefined : session.enable}
              />
            </div>
          )}

          {/* Global gameplay authorisation — one-time USDC approve that
              unlocks every paid game (FPS / Math / Tower Defense / Snake).
              Shown whenever games are configured + a wallet is connected. */}
          {gamesDeployed && walletAddress && (
            <div
              data-no-drag
              style={{
                borderRadius: 12,
                border: gamesAuthorized
                  ? "1px solid rgba(95,207,128,0.25)"
                  : "1px solid rgba(251,191,36,0.3)",
                background: gamesAuthorized
                  ? "rgba(95,207,128,0.05)"
                  : "rgba(251,191,36,0.06)",
                padding: "12px 14px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                  <span
                    style={{
                      fontFamily: "'Geist Mono', monospace",
                      fontSize: 10,
                      letterSpacing: 1.4,
                      color: gamesAuthorized ? "#5fcf80" : "#fbbf24",
                      fontWeight: 600,
                    }}
                  >
                    {gamesAuthorized ? "GAMEPLAY AUTHORIZED" : "AUTHORIZE GAMEPLAY"}
                  </span>
                  <span style={{ fontSize: 11, color: "#8a7f72", lineHeight: 1.4 }}>
                    {gamesAuthorized
                      ? "USDC approved — you can join any paid match instantly."
                      : "One-time approval so Hupper can lock your stake when you join a match. USDC stays in your wallet until then."}
                  </span>
                </div>
                {gamesAuthorized && (
                  <span
                    aria-hidden
                    style={{
                      flexShrink: 0,
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      background: "rgba(95,207,128,0.15)",
                      color: "#5fcf80",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </span>
                )}
              </div>

              {!gamesAuthorized && (
                <button
                  data-no-drag
                  onClick={async () => {
                    try {
                      await approveGames();
                      await allowance.refetch();
                    } catch {
                      /* approveStatus.error already set */
                    }
                  }}
                  disabled={approveStatus.pending}
                  style={{
                    appearance: "none",
                    width: "100%",
                    height: 42,
                    border: "none",
                    borderRadius: 10,
                    cursor: approveStatus.pending ? "not-allowed" : "pointer",
                    background: "#fbbf24",
                    color: "#0a0806",
                    fontFamily: "'Geist Mono', monospace",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    opacity: approveStatus.pending ? 0.6 : 1,
                    transition: "all 160ms ease",
                  }}
                >
                  {approveStatus.pending ? "Approving USDC…" : "Approve USDC (one-time)"}
                </button>
              )}

              {approveStatus.error && (
                <div style={{ fontFamily: "'Geist Mono', monospace", fontSize: 10, color: "#ff7777", lineHeight: 1.4 }}>
                  {approveStatus.error.message}
                </div>
              )}
            </div>
          )}

          {/* Legacy vault (v1 only) */}
          {hasLegacyBalance && (
            <div className="wc-legacy">
              <div className="wc-legacy-row1">
                <div>
                  <div className="wc-legacy-label">LEGACY HUPPER VAULT</div>
                  <div className="wc-legacy-amount">${formatUSDT(vault.value)}<span>USDT</span></div>
                </div>
                <button data-no-drag className="wc-legacy-withdraw" onClick={() => setView("withdraw")}>WITHDRAW</button>
              </div>
              <div className="wc-legacy-desc">Old custody — migrate by withdrawing to your wallet.</div>
            </div>
          )}

          {/* Games-not-configured hint (dev/misconfig only) */}
          {!gamesDeployed && (
            <div className="wc-warn">
              HupperGames not configured on {ACTIVE_CHAIN.name}. Set{" "}
              <code>VITE_HUPPER_GAMES_ADDRESS</code> + <code>VITE_USDC_ADDRESS</code> in your{" "}
              <code>apps/web/.env</code> to wire it up.
            </div>
          )}

          {/* Address */}
          <div className="wc-sect-label">ADDRESS</div>
          <div className="wc-address-row">
            <span className="wc-address-value" title={walletAddress ?? undefined}>{walletAddress ?? "—"}</span>
            <button data-no-drag onClick={copy} disabled={!walletAddress} aria-label="Copy address" title="Copy" className="wc-icon-btn">
              {copied ? <CheckIcon /> : <CopyIcon />}
            </button>
            {explorerUrl && (
              <a data-no-drag href={explorerUrl} target="_blank" rel="noreferrer" aria-label="View on explorer" title="Explorer" className="wc-icon-btn">
                <ExplorerIcon />
              </a>
            )}
          </div>

          {/* Network */}
          <div className="wc-network-row">
            <span className="wc-network-name">
              <span className="wc-network-dot" />
              {ACTIVE_CHAIN.name}
            </span>
            <span className="wc-network-mode">{ACTIVE_CHAIN.paidMode ? "PAID MODE" : "TEST MODE"}</span>
          </div>

          {allowance.error && (
            <div style={{ fontSize: 10, color: "#ff5a5a", opacity: 0.7, fontFamily: "'Geist Mono', monospace" }}>
              allowance read failed: {allowance.error.message}
            </div>
          )}

          {/* Actions */}
          {isV2 ? (
            <>
              <button data-no-drag className="wc-btn-primary tall" onClick={() => setView("deposit")}>
                <PlusIcon /> Top up balance
              </button>
              <div className="wc-actions-3">
                <button data-no-drag className="wc-btn-action" onClick={copy} disabled={!walletAddress} title="Copy address to receive USDC">
                  <span className="wc-btn-icon"><ReceiveIcon /></span>
                  {copied ? "Copied ✓" : "Receive"}
                </button>
                <button data-no-drag className="wc-btn-action" onClick={() => setView("send")} disabled={!canSend} title={!canSend ? "Nothing to send" : "Send USDC to any address"}>
                  <span className="wc-btn-icon"><SendIcon /></span>
                  Send
                </button>
                <button data-no-drag className="wc-btn-action" disabled title="Swap — coming soon">
                  <span className="wc-btn-icon"><SwapIcon /></span>
                  Swap
                </button>
              </div>
            </>
          ) : (
            <div className="wc-actions">
              <button data-no-drag className="wc-btn-primary" onClick={copy} disabled={!walletAddress} title="Copy address to receive USDC">
                <ReceiveIcon /> {copied ? "Copied ✓" : "Receive"}
              </button>
              <button data-no-drag className="wc-btn-secondary" onClick={() => setView("send")} disabled={!canSend} title={!canSend ? "Nothing to send" : "Send USDC to any address"}>
                <SendIcon /> Send
              </button>
            </div>
          )}

          <button data-no-drag className="wc-signout" onClick={() => { onSignOut(); onClose(); }}>
            <SignOutIcon /> Sign out
          </button>
        </div>
      </div>

      {view === "deposit" && (
        <DepositModal walletAddress={walletAddress} onClose={() => setView("wallet")} />
      )}
      {view === "withdraw" && (
        <WithdrawModal walletAddress={walletAddress} onClose={() => setView("wallet")} />
      )}
      {view === "send" && (
        <SendUsdcModal
          walletAddress={walletAddress}
          usdcBalance={usdc.value}
          onClose={() => setView("wallet")}
          onSent={() => {
            usdc.refetch();
            setView("wallet");
          }}
        />
      )}
    </>
  );
}
